'use client'

import { useState, useEffect } from 'react'
import { ClinicaService, ClinicaItem } from '../../lib/services'
import LocationPromptModal from './LocationPromptModal'

function agruparHorarios(horarios: any[]) {
  if (!horarios || !horarios.length) return [];
  const sorted = [...horarios].sort((a, b) => a.dia - b.dia);
  const getTimeStr = (h: any) => `${h.de}-${h.ate}-${h.intervalo_ini || ''}-${h.intervalo_fim || ''}`;
  
  const groups: any[] = [];
  let currentGroup = {
    startDia: sorted[0].dia,
    endDia: sorted[0].dia,
    de: sorted[0].de,
    ate: sorted[0].ate,
    timeStr: getTimeStr(sorted[0])
  };

  for (let i = 1; i < sorted.length; i++) {
    const h = sorted[i];
    if (getTimeStr(h) === currentGroup.timeStr && h.dia === currentGroup.endDia + 1) {
      currentGroup.endDia = h.dia;
    } else {
      groups.push(currentGroup);
      currentGroup = {
        startDia: h.dia,
        endDia: h.dia,
        de: h.de,
        ate: h.ate,
        timeStr: getTimeStr(h)
      };
    }
  }
  groups.push(currentGroup);
  return groups;
}

export default function ClinicasExamesLista({
  estado,
  cidade
}: {
  estado?: string
  cidade?: string
}) {
  const [clinicas, setClinicas] = useState<(ClinicaItem & { distanciaKm?: number })[]>([])
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [hasPromptedLocation, setHasPromptedLocation] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('hasPromptedLocation') === 'true'
    }
    return false
  })
  const [resolvedCoords, setResolvedCoords] = useState<{ lat: number; lng: number } | null | undefined>(undefined)

  useEffect(() => {
    let isMounted = true

    const buscarClinicas = async (lat?: number, lng?: number) => {
      try {
        setLoading(true)
        setMessage('')
        
        let finalData: any[] = []
        let finalMessage = ''

        const cleanCidade = cidade ? cidade.replace(/[-_]/g, ' ') : undefined;

        const trackClarity = (eventName: string, extra?: string) => {
          import('../../lib/clarity').then(({ trackClarityEvent, setClarityTag }) => {
            if (estado) setClarityTag('busca_estado', estado)
            if (cleanCidade) setClarityTag('busca_cidade', cleanCidade)
            if (lat) setClarityTag('has_gps', 'true')
            if (extra) setClarityTag('busca_res_tipo', extra)
            trackClarityEvent(eventName)
          }).catch(() => {})
        }

        if (estado && cleanCidade) {
          let searchLat = lat;
          let searchLng = lng;
          let usedCityFallback = false;
          let displayCityName = cleanCidade.replace(/\b\w/g, (l: string) => l.toUpperCase());

          // Se não temos GPS do usuário, ancoramos o raio de distância no centro da cidade da URL
          if (!lat || !lng) {
             const res = await fetch(
               `https://nominatim.openstreetmap.org/search?format=json&city=${encodeURIComponent(cleanCidade)}&state=${estado}&country=Brasil&limit=1`, 
               { headers: { 
                 'Accept-Language': 'pt-BR',
                 'User-Agent': 'SSTExames/1.0 (contato@demaisaude.com)'
               } }
             ).catch(() => null)
             const geoData = res ? await res.json().catch(() => null) : null
             
             if (geoData && geoData.length > 0) {
               searchLat = parseFloat(geoData[0].lat);
               searchLng = parseFloat(geoData[0].lon);
               usedCityFallback = true;
             }
          }

          // 1. Prioridade Absoluta: Clínicas Exatas da Cidade (com distâncias calculadas se houver coords)
          let exactCityClinics = await ClinicaService.getClinicasFiltradas(
            searchLat && searchLng ? { lat: searchLat, lng: searchLng, estado, cidade: cleanCidade } : { estado, cidade: cleanCidade }
          )

          if (usedCityFallback) {
             exactCityClinics = exactCityClinics.map((c: any) => ({ ...c, distanciaKm: 0 }))
          }

          finalData = exactCityClinics;

          if (exactCityClinics.length > 0) {
            displayCityName = exactCityClinics[0].cidade || displayCityName;
            finalMessage = lat && lng
               ? `Clínicas em ${displayCityName} ordenadas por sua proximidade`
               : `Exibindo clínicas na cidade de ${displayCityName}`;
            trackClarity('clinicas_exames_busca', (lat && lng) ? 'exato_cidade_com_gps' : 'exato_cidade_base');
          } else {
            finalMessage = lat && lng
               ? `Sem clínicas na cidade. Exibindo as opções mais próximas de você no estado:`
               : `Sem clínicas na cidade. Exibindo as mais próximas de ${displayCityName}:`;
            trackClarity('clinicas_exames_busca', (lat && lng) ? 'fallback_distancia_user' : 'fallback_distancia_cidade');
          }
          
          // 2. Transbordo: Se achou menos que 6 (ou zero), completa com clínicas de OUTRAS cidades do estado
          if (finalData.length < 6) {
             const extras = await ClinicaService.getClinicasFiltradas(
                searchLat && searchLng ? { lat: searchLat, lng: searchLng, estado } : { estado }
             )
             // Filtra pra não repetir quem já entrou como exactCity
             const extraFiltered = extras.filter((c: any) => !exactCityClinics.some((ex: any) => ex.id === c.id))
             finalData = [...finalData, ...extraFiltered]
          }
        } 
        else if (estado) {
          finalData = await ClinicaService.getClinicasFiltradas(
            lat && lng ? { lat, lng, estado } : { estado }
          )
          finalMessage = lat ? `Clínicas em ${estado.toUpperCase()} ordenadas por proximidade.` : `Exibindo clínicas em ${estado.toUpperCase()}`
          trackClarity('clinicas_exames_busca', lat ? 'estado_gps' : 'estado_apenas')
        } 
        else {
          if (lat && lng) {
            finalData = await ClinicaService.getClinicasFiltradas({ lat, lng })
            finalMessage = 'Clínicas mais próximas da sua localização atual.'
            trackClarity('clinicas_exames_busca', 'gps_apenas')
          } else {
            finalData = await ClinicaService.getClinicas()
            finalMessage = 'Exibindo clínicas disponíveis pelo Brasil.'
            trackClarity('clinicas_exames_busca', 'geral_sem_filtro')
          }
        }

        if (isMounted) {
          setMessage(finalMessage)
          const formatadas = finalData.slice(0, 20).map((c: any) => ({
            id: c.id,
            nome: c.nome,
            bairro: c.bairro || '',
            cidade: c.cidade,
            uf: c.uf,
            cep: c.cep,
            endereco: c.endereco,
            numero: c.numero,
            latitude: c.latitude,
            longitude: c.longitude,
            geolocalizacao_atualizada_em: c.geolocalizacao_atualizada_em,
            distanciaKm: c.distanciaKm,
            horarios: c.horarios || []
          }))
          setClinicas(formatadas)

          // Background Job Client-Side: Rehidratação de Clínicas defasadas (sem rate limits pois roda 1x por client)
          const dehydrated = formatadas.filter((c: any) => !c.geolocalizacao_atualizada_em);
          if (dehydrated.length > 0) {
            dehydrated.forEach(async (clinica: any, i: number) => {
               try {
                  // Atraso intencional escalonado caso existam várias na mesma página para não dar pico instantâneo do client
                  await new Promise(r => setTimeout(r, i * 1500));
                  
                  const params = new URLSearchParams();
                  if (clinica.endereco) {
                      const street = clinica.numero ? `${clinica.numero} ${clinica.endereco}` : clinica.endereco;
                      params.append('street', street);
                  }
                  if (clinica.cidade) params.append('city', clinica.cidade);
                  if (clinica.uf) params.append('state', clinica.uf);
                  if (clinica.cep) params.append('postalcode', clinica.cep);
                  params.append('country', 'Brasil');
                  params.append('format', 'json');
                  params.append('limit', '1');

                  const queryURL = `https://nominatim.openstreetmap.org/search?${params.toString()}`;
                  const nomRes = await fetch(queryURL, { headers: { 'Accept-Language': 'pt-BR', 'User-Agent': 'SSTExamesFront/1.0' } });
                  const geoData = await nomRes.json().catch(() => null);
                  
                  let updatePayload = {};
                  if (geoData && geoData.length > 0) {
                     updatePayload = { lat: parseFloat(geoData[0].lat), lng: parseFloat(geoData[0].lon) };
                  }
                  
                  await ClinicaService.updateGeolocalizacao(clinica.id, updatePayload);
               } catch (err) {
                  console.warn('Falha silenciosa no Client-Size geocoding:', err);
               }
            });
          }
        }
      } catch (e) {
        console.error('Erro na listagem de clinicas de exame:', e)
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    if (!hasPromptedLocation) {
       setLoading(false)
       setShowLocationModal(true)
       return
    }

    if (resolvedCoords !== undefined) {
      if (resolvedCoords === null) buscarClinicas()
      else buscarClinicas(resolvedCoords.lat, resolvedCoords.lng)
      return
    }

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => buscarClinicas(pos.coords.latitude, pos.coords.longitude),
        (err) => {
          console.log('Geolocalização negada ou não disponível.', err)
          buscarClinicas()
        },
        { timeout: 5000, maximumAge: 600000 }
      )
    } else {
      buscarClinicas()
    }

    return () => { isMounted = false }
  }, [estado, cidade, hasPromptedLocation, resolvedCoords])

  const handleLocationDecision = async (coords: { lat: number; lng: number } | null) => {
    localStorage.setItem('hasPromptedLocation', 'true')
    setHasPromptedLocation(true)
    setShowLocationModal(false)
    setResolvedCoords(coords)
  }

  if (loading) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
        <p className="text-gray-500 font-medium">Buscando clínicas próximas...</p>
      </div>
    )
  }

  if (clinicas.length === 0 && !showLocationModal) return null

  return (
    <div className="w-full">
      <LocationPromptModal 
        isOpen={showLocationModal} 
        onClose={() => setShowLocationModal(false)}
        onAllow={handleLocationDecision}
      />



      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {clinicas.map((clinica) => (
          <div key={clinica.id} className="flex flex-col h-full bg-white rounded-lg shadow-sm border border-green-100 p-4 hover:border-green-400 transition-colors relative overflow-hidden group">
            
            <div className="flex justify-between items-start mb-1">
              <h3 className="font-bold text-gray-900 text-sm leading-tight pr-2">
                {clinica.nome}
              </h3>
            </div>
            
            <p className="text-xs text-gray-600 mb-3 truncate">
              {clinica.bairro} - {clinica.cidade}/{clinica.uf}
            </p>

            {clinica.distanciaKm !== undefined && (
              <div className="mb-3 inline-flex items-center gap-1 text-green-700 text-xs font-bold">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                A {clinica.distanciaKm} km
              </div>
            )}

            {clinica.horarios && clinica.horarios.length > 0 && (
              <div className="mb-4 text-xs text-gray-600 bg-gray-50 p-2 rounded border border-gray-100">
                <span className="font-semibold text-gray-700 block mb-1">Horários de Atendimento:</span>
                <ul className="space-y-0.5">
                  {agruparHorarios(clinica.horarios).map((g, i) => {
                    const dias = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
                    const nomeInicio = dias[g.startDia] || `Dia ${g.startDia}`;
                    const nomeFim = dias[g.endDia] || `Dia ${g.endDia}`;
                    const labelDia = g.startDia === g.endDia ? nomeInicio : `${nomeInicio} a ${nomeFim}`;
                    return (
                      <li key={i} className="flex justify-between border-b border-gray-100 last:border-0 pb-0.5 last:pb-0">
                        <span>{labelDia}</span>
                        <span className="font-medium text-gray-800">{g.de} {g.ate ? `às ${g.ate}` : ''}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <button
              onClick={(e) => {
                e.preventDefault()
                localStorage.setItem('preSelectedClinica', String(clinica.id));
                localStorage.setItem('preSelectedClinicaNome', clinica.nome);
                const form = document.getElementById('formularioAgendamento');
                if (form) {
                  form.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="mt-auto w-full bg-green-600 text-white font-bold py-3 px-4 rounded-lg shadow-md hover:bg-green-700 hover:shadow-lg hover:-translate-y-0.5 transition-all text-sm flex justify-center items-center gap-2 group tracking-wide"
            >
              Agendar Aqui
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
